/* ==========================================================================
   XELPHORA — carousel.js
   <x-carousel> progressive enhancement for a native scroll-snap track.

   The track scrolls on its own with zero JavaScript. This file only:
   - reveals and wires the prev/next buttons
   - keeps their disabled state in sync with scroll position
   - drives the progress bar
   - hides the controls entirely when there is nothing to scroll

   Loaded with `defer` by any section that renders a carousel, so pages
   without one never download it.
   ========================================================================== */

(() => {
  'use strict';

  class Carousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      this.controls = this.querySelector('[data-carousel-controls]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.progress = this.querySelector('[data-carousel-progress]');

      this.onScroll = this.onScroll.bind(this);
      this.ticking = false;

      this.prev?.addEventListener('click', () => this.page(-1));
      this.next?.addEventListener('click', () => this.page(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      // A slide's width depends on viewport and on lazy images settling, so
      // re-evaluate on any size change rather than measuring once.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.sync());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onScroll);
      }

      this.sync();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.track?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    /** Distance of one "page" — as many whole slides as currently fit. */
    get pageDistance() {
      const slide = this.track.firstElementChild;
      if (!slide) return this.track.clientWidth;

      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const step = slide.getBoundingClientRect().width + gap;

      // At least one slide, at most a full viewport of them.
      const perPage = Math.max(1, Math.floor(this.track.clientWidth / step));
      return step * perPage;
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    page(direction) {
      this.track.scrollBy({ left: this.pageDistance * direction, behavior: 'smooth' });
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.sync();
        this.ticking = false;
      });
    }

    sync() {
      const max = this.maxScroll;

      // Nothing to scroll: drop the controls out of the layout and the a11y
      // tree rather than leaving two dead buttons on the page.
      const overflows = max > 1;
      this.controls?.toggleAttribute('hidden', !overflows);
      if (!overflows) return;

      const x = this.track.scrollLeft;
      // 1px tolerance: fractional scroll positions never hit the exact bound.
      if (this.prev) this.prev.disabled = x <= 1;
      if (this.next) this.next.disabled = x >= max - 1;

      if (this.progress) {
        const visibleRatio = this.track.clientWidth / this.track.scrollWidth;
        const width = Math.max(0.08, Math.min(1, visibleRatio));
        const travel = (1 - width) * (x / max);
        this.progress.style.setProperty('--progress-width', `${width * 100}%`);
        this.progress.style.setProperty('--progress-x', `${(travel / width) * 100}%`);
      }
    }
  }

  if (!customElements.get('x-carousel')) customElements.define('x-carousel', Carousel);
})();
